/**
 * ArrayCell Component
 * 
 * Renders Array values with:
 * - Item count display: "[3 items]"
 * - Preview of first items for simple arrays
 * - Click to expand for full content
 * - Special handling for arrays of Pointers
 */

import { CellRendererProps, ParsePointer } from '../../../../../types/AppPanel';

const MAX_PREVIEW_ITEMS = 2;
const MAX_PREVIEW_LENGTH = 30;

export default function ArrayCell({ 
  value, 
  onExpand,
  onPointerClick 
}: CellRendererProps<unknown[] | null | undefined>) {
  // Handle null/undefined
  if (value === null) {
    return <span className="text-white/30 italic">(null)</span>;
  }
  if (value === undefined) {
    return <span className="text-white/30 italic">(undefined)</span>;
  }
  if (!Array.isArray(value)) {
    return <span className="text-white/30 italic">(invalid array)</span>;
  }
  if (value.length === 0) {
    return <span className="text-white/30 italic">[]</span>;
  }

  // Check if it's an array of Pointers
  const isPointerArray = value.length > 0 && 
    typeof value[0] === 'object' && 
    value[0] !== null &&
    (value[0] as ParsePointer).__type === 'Pointer';

  if (isPointerArray) {
    return (
      <div className="flex flex-wrap gap-1">
        {(value as ParsePointer[]).slice(0, MAX_PREVIEW_ITEMS).map((ptr, idx) => (
          <button
            key={idx}
            onClick={() => onPointerClick?.(ptr)}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full 
                       bg-[#208AEC]/20 text-[#208AEC] text-xs
                       hover:bg-[#208AEC]/30 transition-colors cursor-pointer"
            title={`${ptr.className}: ${ptr.objectId}`}
          >
            →{ptr.objectId?.slice(0, 6)}
          </button>
        ))}
        {value.length > MAX_PREVIEW_ITEMS && (
          <button
            onClick={onExpand}
            className="inline-flex items-center px-1.5 py-0.5 rounded-full 
                       bg-white/10 text-white/60 text-xs
                       hover:bg-white/20 transition-colors cursor-pointer"
          >
            +{value.length - MAX_PREVIEW_ITEMS}
          </button>
        )}
      </div>
    );
  }

  // For simple arrays, show preview or just count
  const preview = JSON.stringify(value);
  if (preview.length <= MAX_PREVIEW_LENGTH) {
    return (
      <span 
        className="text-white/70 font-mono text-xs cursor-pointer hover:text-[#208AEC]"
        onClick={onExpand}
        title="Click to expand"
      >
        {preview}
      </span>
    );
  }

  return (
    <button
      onClick={onExpand}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full 
                 bg-white/10 text-white/70 text-xs font-medium
                 hover:bg-white/20 transition-colors cursor-pointer"
      title="Click to view array contents"
    >
      <span className="font-mono">[</span>
      {value.length} {value.length === 1 ? 'item' : 'items'}
      <span className="font-mono">]</span>
    </button>
  );
}

